import 'components/discovery/store-locator/components/store-search-field/store-search-field.js'
import 'components/discovery/store-locator/components/store-list/store-list.js'
import 'components/discovery/store-locator/components/store-map/store-map.js'
import 'components/discovery/store-locator/components/selected-store-card/selected-store-card.js'
import { customElement, property, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, nothing } from 'lit'

import { InternalStore, Store, StoreModel } from 'components/discovery/store-locator/typing/store-locator.js'
import {
  defaultStoresList,
  STORE_LOCATOR_PROXY_PATH,
  STORE_LOCATOR_STORE,
  STORES_PATH,
  StoreLocatorStore,
  StoresList
} from 'components/discovery/store-locator/store-locator-store.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'

import { style } from './store-locator.style.js'
import typia from 'typia'
import { logValidationFailure } from 'components/shared/parsing/parsing.js'
import { classMap } from 'lit/directives/class-map.js'
import { StoreLocatorMobileView } from 'components/discovery/store-locator/typing/internal.js'
import { BREAKPOINTS, ConcreteBreakpoints } from '@cloudsuite/mosaic-shared/scripts/breakpoints.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'

initializeIconsByRole({
  'csc-store-locator.show-map': { name: 'map', style: 'regular' },
  'csc-store-locator.show-list': { name: 'list', style: 'regular' },
})

@customElement('csc-store-locator')
export class CSCStoreLocator extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Object, attribute: 'stores' })
  storesProp: Store[] = []

  private deprecatedStoresPath = ''

  private deprecatedProxyPath = ''

  /** @deprecated The stores URL is built from the base URL. */
  @property({ type: String, attribute: 'stores-path' })
  set storesPath(value: string) {
    console.warn('<csc-store-locator> "stores-path" is deprecated, remove it: the stores URL is built from the base URL.')
    this.deprecatedStoresPath = value
  }

  get storesPath() {
    return this.deprecatedStoresPath
  }

  /** @deprecated The store locator proxy URL is built from the base URL. */
  @property({ type: String, attribute: 'proxy-path' })
  set proxyPath(value: string) {
    console.warn('<csc-store-locator> "proxy-path" is deprecated, remove it: the proxy URL is built from the base URL.')
    this.deprecatedProxyPath = value
  }

  get proxyPath() {
    return this.deprecatedProxyPath
  }

  @property({ type: String })
  country: string = ''

  @property({ type: Boolean, attribute: 'show-selected-store-border' })
  showSelectedStoreBorder = false

  @property({ type: Boolean, attribute: 'show-paragraph' })
  showParagraph = false

  @property({ type: Boolean, attribute: 'show-paragraph-description' })
  showParagraphDescription = false

  @inject(STORE_LOCATOR_STORE)
  storeLocatorStore!: StoreLocatorStore

  @inject(BREAKPOINTS)
  private breakpoints!: ConcreteBreakpoints

  @state()
  smallScreen: boolean = false

  @state()
  currentMobileView: StoreLocatorMobileView = 'list'

  @state()
  stores: StoresList = defaultStoresList

  @state()
  selectedStore!: InternalStore | null

  constructor() {
    super()

    this.breakpoints.matchMedia(breakpoints.medium)

    createEffect(() => {
      this.stores = this.storeLocatorStore.stores()
      this.selectedStore = this.storeLocatorStore.selectedStore()

      this.smallScreen = this.breakpoints.small()

      this.currentMobileView = this.storeLocatorStore.viewToShowOnMobile()
    })
  }

  override connectedCallback() {
    super.connectedCallback()

    if (this.storesProp.length > 0) {
      const validated = typia.validate<Array<Store>>(this.storesProp)
      if (validated.success) {
        const parsedStores = validated.data.map(store => new StoreModel().with(store).parseToInternal())
        this.storeLocatorStore.preloadStores(parsedStores)
      } else {
        logValidationFailure('<csc-store-locator> preloaded stores', validated.errors)
      }
    }

    void this.configurePaths()

    if (this.country) {
      this.storeLocatorStore.setCountry(this.country)
    }
  }

  private configurePaths = async () => {
    const storesPath = this.deprecatedStoresPath || (await createUrl(STORES_PATH)).href
    if (!this.isConnected) return
    this.storeLocatorStore.setStoresPath(storesPath)

    const proxyPath = this.deprecatedProxyPath || (await createUrl(STORE_LOCATOR_PROXY_PATH)).href
    if (!this.isConnected) return
    this.storeLocatorStore.setProxy(proxyPath)
  }

  private toggleShowMap = () => {
    this.storeLocatorStore.toggleListAndMapViews()
  }

  private renderMobileToggle = () => html`
    <cse-chip class="toggle-button" @click="${this.toggleShowMap}">
      ${this.currentMobileView === 'map' ? html`
        <cse-icon slot="prefix" icon-role="csc-store-locator.show-list"></cse-icon>
        ${t('show_store_list', { ns: 'csc-store-locator', default: 'Show list' })}
      ` : html`
        <cse-icon slot="prefix" icon-role="csc-store-locator.show-map"></cse-icon>
        ${t('show_map', { ns: 'csc-store-locator', default: 'Show map' })}
      `}
    </cse-chip>
  `

  shouldRenderNoStoresWarning() {
    return this.stores.stores.length === 0 && this.stores.origin === 'headless'
  }

  private renderStoresView = () => html`
    <cse-group
      direction="column"
      class="${classMap({
        'list-view': true,
        'desktop': !this.smallScreen,
        'hidden': this.smallScreen && this.currentMobileView !== 'list'
      })}"
    >
      ${this.shouldRenderNoStoresWarning()
        ? this.renderWarning()
        : html`
          <csc-store-search-field></csc-store-search-field>
          <csc-store-list></csc-store-list>
        `
      }
    </cse-group>
  `

  private renderWarning = () => html`
    <cse-alert variant="warning">
      ${t('no_locations_configured_title', { ns: 'csc-store-locator', default: 'There are no locations yet' })}
      <span slot="description">
        ${t('no_locations_configured_subtext', { ns: 'csc-store-locator', default: 'Please try again at a later time' })}
      </span>
    </cse-alert>
  `

  private renderMapView = () => html`
    <div class="${classMap({
      'map-view': true,
      'desktop': !this.smallScreen,
      'hidden': this.smallScreen && this.currentMobileView !== 'map'
    })}">
      <csc-store-map></csc-store-map>

      ${this.selectedStore != null ? html`
        <csc-selected-store-card ?show-border="${this.showSelectedStoreBorder}"></csc-selected-store-card>
      ` : nothing}
    </div>
  `

  override render() {
    return html`
      <div class="csc-store-locator-container">
        ${this.showParagraph ? html`
          <cse-text-group>
            <cse-text slot="heading" variant="heading-1">
              ${t('store_locator_title', { ns: 'csc-store-locator', default: 'Store locator' })}
            </cse-text>
            ${this.showParagraphDescription ? html`
              <cse-text variant="body">${t('store_locator_subtitle', { ns: 'csc-store-locator', default: 'Find our stores here.' })}</cse-text>
            ` : nothing }
          </cse-text-group>
        ` : nothing}
        <cse-group
          class="csc-store-locator"
          direction="${this.smallScreen ? 'column' : 'row'}"
        >
          ${this.smallScreen ? this.renderMobileToggle() : nothing}
          ${this.renderStoresView()}
          ${this.renderMapView()}
        </cse-group>
      </div>
    `
  }
}
